import type { Expr, Where } from './ast'
import { ScriptError, UNITS, type Dim, type Unit } from './lexer'

export type Value =
  | { readonly kind: 'number'; readonly value: number; readonly dim: Dim }
  | { readonly kind: 'text'; readonly value: string }
  | { readonly kind: 'bool'; readonly value: boolean }
  | { readonly kind: 'nil' }
  | { readonly kind: 'array'; readonly items: readonly Value[] }
  /** A read-only record — a session, a set, a target. Reading a field it lacks is an error. */
  | { readonly kind: 'obj'; readonly name: string; readonly fields: ReadonlyMap<string, Value> }
  | {
      readonly kind: 'fn'
      readonly params: readonly string[]
      readonly body: Expr
      readonly scope: Scope
    }
  /** A primary that spans two values, or is open at the top as an `amrap` is. */
  | {
      readonly kind: 'span'
      readonly low: number
      readonly high: number
      readonly open: boolean
      readonly dim: Dim
    }

export const NIL: Value = { kind: 'nil' }

export const num = (value: number, dim: Dim = 'none'): Value => ({ kind: 'number', value, dim })

export const bool = (value: boolean): Value => ({ kind: 'bool', value })

export const txt = (value: string): Value => ({ kind: 'text', value })

export const arr = (items: readonly Value[]): Value => ({ kind: 'array', items })

export const obj = (name: string, fields: Record<string, Value>): Value => ({
  kind: 'obj',
  name,
  fields: new Map(Object.entries(fields)),
})

/** Names in hand while a block runs. Locals only — there is no state to write to. */
export class Scope {
  private readonly values = new Map<string, Value>()
  private readonly fixed = new Set<string>()

  private readonly parent: Scope | null

  constructor(parent: Scope | null = null) {
    this.parent = parent
  }

  child(): Scope {
    return new Scope(this)
  }

  declare(name: string, value: Value, mutable: boolean): void {
    this.values.set(name, value)
    if (!mutable) {
      this.fixed.add(name)
    }
  }

  get(name: string): Value | undefined {
    return this.values.has(name) ? this.values.get(name) : this.parent?.get(name)
  }

  assign(name: string, value: Value, at: Where): void {
    if (this.values.has(name)) {
      if (this.fixed.has(name)) {
        throw new ScriptError(
          at.line,
          at.column,
          `\`${name}\` was declared with const and cannot be changed`,
        )
      }
      this.values.set(name, value)
      return
    }
    if (!this.parent) {
      throw new ScriptError(at.line, at.column, `\`${name}\` is not a name in scope`)
    }
    this.parent.assign(name, value, at)
  }
}

export function truthy(value: Value): boolean {
  switch (value.kind) {
    case 'bool':
      return value.value
    case 'number':
      return value.value !== 0
    case 'text':
      return value.value.length > 0
    case 'nil':
      return false
    default:
      return true
  }
}

export function describeValue(value: Value): string {
  switch (value.kind) {
    case 'number':
      return 'a number'
    case 'text':
      return 'text'
    case 'bool':
      return 'true or false'
    case 'nil':
      return 'nothing'
    case 'array':
      return 'a list'
    case 'obj':
      return value.name
    case 'fn':
      return 'a function'
    case 'span':
      return 'a range'
  }
}

/** A number written with its unit, the way the source writes it. */
export function formatNumber(value: number, unit: Unit): string {
  const shown = value / unit.factor
  const rounded = Math.round(shown * 1000) / 1000
  return `${rounded}${unit.suffix}`
}

/** The unit a value of this dimension is written in when nothing said otherwise. */
export function defaultUnit(dim: Dim): Unit {
  switch (dim) {
    case 'weight':
      return UNITS.kg
    case 'time':
      return UNITS.seconds
    case 'distance':
      return UNITS.meters
    case 'none':
      return UNITS.reps
  }
}

function nameOf(dim: Dim): string {
  switch (dim) {
    case 'none':
      return 'A plain number'
    case 'weight':
      return 'A weight'
    case 'time':
      return 'A time'
    case 'distance':
      return 'A distance'
  }
}

export function text(value: Value): string {
  switch (value.kind) {
    case 'text':
      return value.value
    case 'number':
      return formatNumber(value.value, defaultUnit(value.dim))
    case 'bool':
      return String(value.value)
    case 'nil':
      return ''
    case 'span':
      return value.open ? `amrap(${value.low})` : `${value.low}-${value.high}`
    default:
      return describeValue(value)
  }
}

/**
 * Arithmetic that keeps units honest: the same unit adds, a plain number scales, and anything
 * else is a mistake worth naming rather than a number nobody can explain.
 */
export function arithmetic(op: string, left: Value, right: Value, at: Where): Value {
  if (left.kind !== 'number' || right.kind !== 'number') {
    if (op === '+' && (left.kind === 'text' || right.kind === 'text')) {
      return txt(text(left) + text(right))
    }
    throw new ScriptError(
      at.line,
      at.column,
      `\`${op}\` works on numbers, not on ${describeValue(left)} and ${describeValue(right)}`,
    )
  }
  switch (op) {
    case '+':
    case '-': {
      if (left.dim !== right.dim) {
        throw new ScriptError(
          at.line,
          at.column,
          `${nameOf(left.dim)} and ${nameOf(right.dim)} cannot be added together`,
        )
      }
      return num(op === '+' ? left.value + right.value : left.value - right.value, left.dim)
    }
    case '*': {
      if (left.dim === 'none') return num(left.value * right.value, right.dim)
      if (right.dim === 'none') return num(left.value * right.value, left.dim)
      throw new ScriptError(
        at.line,
        at.column,
        `${nameOf(left.dim)} cannot be multiplied by ${nameOf(right.dim)}`,
      )
    }
    case '/': {
      if (right.dim === 'none') return num(left.value / right.value, left.dim)
      if (left.dim === right.dim) return num(left.value / right.value, 'none')
      throw new ScriptError(
        at.line,
        at.column,
        `${nameOf(left.dim)} cannot be divided by ${nameOf(right.dim)}`,
      )
    }
    case '%':
      return num(left.value % right.value, left.dim)
    default:
      throw new ScriptError(at.line, at.column, `\`${op}\` is not an operator here`)
  }
}

export function compare(op: string, left: Value, right: Value, at: Where): boolean {
  if (left.kind === 'number' && right.kind === 'number') {
    switch (op) {
      case '<':
        return left.value < right.value
      case '>':
        return left.value > right.value
      case '<=':
        return left.value <= right.value
      case '>=':
        return left.value >= right.value
      default:
        throw new ScriptError(at.line, at.column, `\`${op}\` is not a comparison`)
    }
  }
  throw new ScriptError(
    at.line,
    at.column,
    `\`${op}\` compares numbers, not ${describeValue(left)} and ${describeValue(right)}`,
  )
}

/** Strict equality, which is the only equality the language has. */
export function same(left: Value, right: Value): boolean {
  if (left.kind === 'number' && right.kind === 'number') {
    return left.value === right.value && left.dim === right.dim
  }
  if (left.kind === 'text' && right.kind === 'text') return left.value === right.value
  if (left.kind === 'bool' && right.kind === 'bool') return left.value === right.value
  if (left.kind === 'nil' && right.kind === 'nil') return true
  return left === right
}
